import { Dispatch, SetStateAction, useState } from 'react'
import { EMPTY_ELEMENT_LIST_FILTERS, type ElementListFilters } from '../components/ListFilterButton'
import { DEFAULT_SORT_DIR, type SortDir, type SortMode } from '../types'

export type ListViewState = {
  sortMode: SortMode
  setSortMode: Dispatch<SetStateAction<SortMode>>
  sortDir: SortDir
  setSortDir: Dispatch<SetStateAction<SortDir>>
  filterText: string
  setFilterText: Dispatch<SetStateAction<string>>
  filters: ElementListFilters
  setFilters: Dispatch<SetStateAction<ElementListFilters>>
  expanded: Record<string, boolean>
  setExpanded: Dispatch<SetStateAction<Record<string, boolean>>>
}

/**
 * View state for a Results2 element list — sort, text/popover filters and tree expansion.
 *
 * Owned by Results2ElementList (above the keyed Form2) rather than inside the form, so it survives
 * the form remount that happens whenever new `items` arrive — e.g. the autosave echo during
 * drag-and-drop grouping, which would otherwise reset the view (sort back to ETA desc, filters
 * cleared, groups re-expanded) mid-grouping. Per-tab reset is still preserved because
 * Results2ListPane keys the whole list per tab.
 *
 * `onlyWarn`/`filter3d` are intentionally not here — they live in useAppStore and already survive
 * the remount as global preferences.
 */
export const useListViewState = (): ListViewState => {
  const [sortMode, setSortMode] = useState<SortMode>('eta')
  const [sortDir, setSortDir] = useState<SortDir>(DEFAULT_SORT_DIR.eta)
  const [filterText, setFilterText] = useState('')
  const [filters, setFilters] = useState<ElementListFilters>(EMPTY_ELEMENT_LIST_FILTERS)
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})

  return {
    sortMode,
    setSortMode,
    sortDir,
    setSortDir,
    filterText,
    setFilterText,
    filters,
    setFilters,
    expanded,
    setExpanded,
  }
}
